<template>
    <div class="listing-layout flex-column hp-10">
        <!-- LOADER -->
        <preloader :display="loading" class="preloader prelative" :background="false" />

        <!-- HEADER -->
        <header class="flex-level mb-4 h-4">
            <!-- TITLE -->
            <h1 v-if="$slots['header'] != '' || metaTitle != ''" class="m-0">
                <slot name="header">{{ metaTitle }}</slot>
            </h1>

            <!-- ACTIONS -->
            <div class="flex-right">
                <ul class="action-nav">
                    <li v-show="hasKpis">
                        <div class="flex-level">
                            <p class="m-0 mr-2">{{ $l10n.__('Afficher les statistiques') }}</p>
                            <CompassSwitch
                                :modelValue="displayKpi"
                                size="medium"
                                @click="toggleKPI"
                            />
                        </div>
                    </li>
                    <li v-if="enableExport">
                        <el-dropdown placement="bottom-end">
                            <CompassButton
                                :label="$t.__('common.labels.actions')"
                                type="secondary"
                                dropdown
                            />
                            <template #dropdown>
                                <el-dropdown-menu>
                                    <template v-for="exportFormat in exportFormats">
                                        <el-dropdown-item :class="{ disable: !permissions.export }">
                                            <a
                                                v-if="permissions.export"
                                                href="#"
                                                @click="
                                                    $store.commit(
                                                        `${listinghash}/toggleDisplay`,
                                                        'export',
                                                    );
                                                    $store.commit(
                                                        `${listinghash}/updateExportType`,
                                                        exportFormat,
                                                    );
                                                "
                                            >
                                                {{
                                                    $l10n.__('Export ' + exportFormat.toUpperCase())
                                                }}
                                            </a>
                                            <a
                                                v-else
                                                href="#"
                                                @click="
                                                    $store.dispatch(
                                                        `${listinghash}/permissionDenied`,
                                                    )
                                                "
                                            >
                                                {{
                                                    $l10n.__('Export ' + exportFormat.toUpperCase())
                                                }}
                                            </a>
                                        </el-dropdown-item>
                                    </template>
                                    <el-dropdown-item
                                        v-for="button in secondaryButtons"
                                        :key="button.label"
                                        :class="{ disable: button.disabled }"
                                    >
                                        <a
                                            v-if="button.disabled"
                                            @click="
                                                $store.dispatch(`${listinghash}/permissionDenied`)
                                            "
                                        >
                                            {{ button.label }}
                                        </a>
                                        <a
                                            v-else-if="button.event != undefined"
                                            @click="$emit(button.event)"
                                        >
                                            {{ button.label }}
                                        </a>
                                        <a
                                            v-else
                                            :href="button.href"
                                            @click="button.click != undefined ? button.click : ''"
                                        >
                                            {{ button.label }}
                                        </a>
                                    </el-dropdown-item>
                                </el-dropdown-menu>
                            </template>
                        </el-dropdown>
                    </li>
                    <li v-for="button in primaryButtons" :key="button.label">
                        <el-tooltip
                            :content="$t.__('common.labels.no_necessary_rights')"
                            placement="top"
                            :disabled="!button.disabled"
                        >
                            <span class="wrapper">
                                <CompassButton
                                    v-if="button.type == 'vuejs'"
                                    :label="button.label"
                                    :disabled="button.disabled"
                                    @click="
                                        callBus(button.url.event, button.disabled, {
                                            linkedid: button.linkedid,
                                            linkedtype: button.linkedtype,
                                        })
                                    "
                                />
                                <CompassButton
                                    v-else-if="button.type == 'function'"
                                    :label="button.label"
                                    :disabled="button.disabled"
                                    @click="
                                        handle_function_call(
                                            button.url.event,
                                            {
                                                linkedid: button.linkedid,
                                                linkedtype: button.linkedtype,
                                            },
                                            button.disabled,
                                        )
                                    "
                                />
                                <CompassButton
                                    v-else-if="button.event != undefined"
                                    :label="button.label"
                                    :disabled="button.disabled"
                                    :data-bot="button.databot ? button.databot : ''"
                                    @click="button.disabled ? null : $emit(button.event)"
                                />
                                <CompassButton
                                    v-else
                                    :label="button.label"
                                    :disabled="button.disabled"
                                    @click="button.disabled ? null : goToUrl(getButtonHref(button))"
                                />
                            </span>
                        </el-tooltip>
                    </li>
                </ul>
            </div>
        </header>
        <!-- HEADER END -->

        <!-- BODY -->
        <div class="listing prelative flex-column ov-reset mh-0 flex">
            <kpi-list
                v-if="displayKpi"
                class="kip-list"
                :kpis="kpis"
                type="listing"
                :source="linkedtype || name"
            ></kpi-list>

            <!-- LISTING TOOLS -->
            <div class="listing-tools flex-level no-shrink mb-2">
                <!-- FILTER -->
                <div class="flex flex-level flex-top">
                    <CompassButton
                        v-if="enableFiltering"
                        :label="$t.__('common.actions.filter')"
                        @click.prevent="$store.dispatch(`${listinghash}/toggleDisplay`, 'filters')"
                    />
                    <filters-preview v-if="!display.filters" :listinghash="listinghash" />
                </div>

                <!-- PAGINATION -->
                <paginator v-if="!isMap" :pagination="pagination" @pagination:update="goToPage" />

                <!-- OPTIONS -->
                <CompassMenu
                    v-if="enableOptions && !isMap"
                    trigger="hover"
                    customClass="listing-menu-settings"
                >
                    <CompassIconButton icon="settings-fill" size="small" />
                    <template #dropdown>
                        <CompassMenuItem
                            :label="$l10n.__('Gestion des lignes')"
                            @click="$store.commit(`${listinghash}/toggleDisplay`, 'table')"
                        />
                        <CompassMenuItem
                            :label="$l10n.__('Gestion des colonnes')"
                            @click="$store.commit(`${listinghash}/toggleDisplay`, 'columns')"
                        />
                    </template>
                </CompassMenu>
                <CompassToggleButtonGroup
                    v-if="switchDisplay && switchDisplay.length > 0"
                    :modelValue="activeToggleButton"
                    exclusive
                    size="small"
                    class="ml-2"
                >
                    <CompassTooltip
                        v-for="(switchButton, switchIndex) in switchDisplay"
                        :key="switchIndex"
                        :content="switchButton.label"
                        :arrow="false"
                    >
                        <CompassToggleButton
                            :value="switchButton.id"
                            :icon="switchButton.icon"
                            :data-upsell="
                                switchButton.id === 'map-view' ? 'map_listing' : undefined
                            "
                            @click="
                                handle_function_call(
                                    switchButton.action,
                                    switchButton.id,
                                    switchButton.selected,
                                )
                            "
                        />
                    </CompassTooltip>
                </CompassToggleButtonGroup>
            </div>
            <!-- LISTING TOOLS END -->

            <!-- LISTING & FILTER PANEL -->
            <div class="flex-row mh-0 flex">
                <!-- FILTER PANEL -->
                <div
                    v-show="display.filters && enableFiltering"
                    class="leftpane well well-no-shadow mr-4 flex-column"
                >
                    <filters-pane v-if="!filtersComponent" :listinghash="listinghash" />
                    <company-directory-filters v-else :data="params" :listinghash="listinghash" />
                </div>

                <!-- COLUMN PANEL -->
                <transition name="slide-fade">
                    <listing-columns v-if="display.columns" :listinghash="listinghash" />
                </transition>

                <!-- DISPLAY OPTION PANEL -->
                <transition name="slide-fade">
                    <listing-display v-if="display.table" :listinghash="listinghash" />
                </transition>

                <!-- LISTING -->
                <div class="listingContainer br flex flex-column">
                    <!-- <div class="listingContent prelative"> -->

                    <!-- ALERT -->
                    <div
                        v-if="alert.enabled && !!alert.text && alert.text.length > 0"
                        class="placeholder-alert placeholder primary-05-bkg b-1-primary-10 p-0 mb-2"
                    >
                        <p class="m-0 fs-3 aleft pl-3">{{ alert.text }}</p>
                    </div>

                    <!-- EMPTY RESULT -->
                    <div
                        v-if="
                            !isMap &&
                            !error &&
                            !loading &&
                            enableFiltering &&
                            Object.keys(rows).length == 0
                        "
                        class="placeholder"
                    >
                        <p class="acenter">
                            {{ $l10n.__('Aucun résultat ne correspond aux filtres appliqués') }}
                        </p>
                        <CompassButton
                            :label="$t.__('common.actions.reset')"
                            @click="$store.dispatch(`${listinghash}/resetListing`)"
                        />
                    </div>

                    <!-- EMPTY RESULT / NO FILTERING -->
                    <div
                        v-if="
                            !error && !loading && !enableFiltering && Object.keys(rows).length == 0
                        "
                        class="placeholder"
                    >
                        <p class="acenter">
                            {{ emptyResultPlaceholder }}
                        </p>
                    </div>

                    <!-- ERROR -->
                    <div v-if="error" class="placeholder">
                        <p class="acenter">
                            {{
                                $l10n.__(
                                    'Une erreur est survenue, veuillez réinitialiser la liste des filtres',
                                )
                            }}
                        </p>
                        <CompassButton
                            :label="$t.__('common.actions.reset')"
                            @click="$store.dispatch(`${listinghash}/resetListing`)"
                        />
                    </div>

                    <!-- TABLE COMPONENT -->
                    <ListingTableElement
                        v-if="!isMap && Object.keys(rows).length > 0"
                        ref="listingTable"
                        :listinghash="listinghash"
                        :rows="rows"
                        :columns="activeColumns"
                    />

                    <listing-map
                        v-if="isMap && !error"
                        :centermap="centermap"
                        :rows="rows"
                        :maptoken="maptoken"
                        :listinghash="listinghash"
                    ></listing-map>

                    <!-- </div> -->
                </div>
                <!-- LISTING END -->
            </div>
            <!-- LISTING  & FILTER PANEL END -->

            <listing-favoritesform
                v-if="display.favoritesForm"
                :listinghash="listinghash"
                :favoriteData="favoriteData"
            />

            <export-modal
                v-if="display.export"
                :listinghash="listinghash"
                :exportType="display.exportType"
                :exportOptions="exportOptions"
                :hasKpis="hasKpis && enableKpisOnExport"
            />
        </div>
        <!-- BODY END -->
    </div>
</template>

<script>
import Feature from 'Services/Feature';
import Env from 'Services/Env';
import ListingMap from './ListingMap.vue';
import FiltersPreview from './FiltersPreview.vue';
import FiltersPane from './FiltersPane.vue';
import Paginator from '../utilities/Paginator.vue';
import ListingTableElement from './ListingTableElement.vue';
import ListingFavoritesform from './favorites/ListingFavoriteForm.vue';
import ListingColumns from './ListingColumns.vue';
import ListingDisplay from './ListingDisplay.vue';
import KpiList from 'vcomponents/utilities/KpiList.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import CompanyDirectoryFilters from 'vcomponents/companysearch/CompanyDirectoryFilters.vue';
import ListingModule from 'vueDir/store/listing';
import MapModule from 'stores/maplisting';
import ApiListingModule from 'stores/apiListing';
import ExportModal from './ExportModal.vue';
import { guid, goToUrl } from 'vueDir/helpers';
import { mapState } from 'vuex';
import _ from 'lodash';
import ListingConfig from '../listing/config';
import { buildUrl } from '../../helpers';

const ConfigListings = new ListingConfig();

import LocalStorage from 'Core/commons/LocalStorage';
import { createNamespacedHelpers } from 'vuex';

const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: {
        'company-directory-filters': CompanyDirectoryFilters,
        Preloader,
        FiltersPane,
        Paginator,
        ListingTableElement,
        FiltersPreview,
        ListingColumns,
        ListingDisplay,
        ExportModal,
        ListingFavoritesform,
        KpiList,
        ListingMap,
    },
    props: {
        endpoint: {
            type: String,
            required: true,
        },
        params: {
            type: Object,
            required: false,
            default: function () {
                return {};
            },
        },
        linkedtype: {
            type: String,
        },
        prefilter: {
            type: Object,
            required: false,
            default: function () {
                return {};
            },
        },
        enableFiltering: {
            type: Boolean,
            required: false,
            default: true,
        },
        enableExport: {
            type: Boolean,
            required: false,
            default: true,
        },
        enableKpisOnExport: {
            type: Boolean,
            required: false,
            default: true,
        },
        exportOptions: {
            type: Object,
            required: false,
            default: function () {
                return {};
            },
        },
        exportFormats: {
            type: Array,
            required: false,
            default: function () {
                return ['csv', 'pdf'];
            },
        },
        favorite: {
            required: false,
            type: Number,
            default: null,
        },
        favoritesName: {
            required: false,
            type: String,
            default: '',
        },
        name: {
            required: false,
            type: String,
            default: '',
        },
        filter: {
            required: false,
            type: Object,
            default() {
                return {};
            },
        },
        buttons: {
            required: false,
            type: Array,
            default() {
                return [];
            },
        },
        useFavorites: {
            type: Boolean,
            default: true,
        },
        savedSearchesUrl: {
            type: String,
            required: false,
        },
        filtersComponent: {
            type: String,
            required: false,
        },
        enableOptions: {
            type: Boolean,
            required: false,
            default: true,
        },
        switchDisplay: {
            type: [Array, Boolean],
            required: false,
            default: false,
        },
        listingMapping: {
            type: Object,
            required: false,
        },
        exportUrl: {
            required: false,
            type: String,
            default: null,
        },
        url: {
            required: false,
            type: String,
            default: null,
        },
        metaUrl: {
            required: false,
            type: String,
            default: null,
        },
        emptyResultPlaceholder: {
            required: false,
            type: String,
            default: window.i18n.__('Aucun élément lié'),
        },
        storeModule: {
            required: false,
            type: Object,
        },
        aggregations: {
            required: false,
            type: Array,
            default() {
                return [];
            },
        },
        enableContext: {
            required: false,
            type: Boolean,
            default: false,
        },
        alert: {
            required: false,
            type: Object,
            default: () => {
                return { text: null, enabled: false };
            },
        },
        relatedPermissions: {
            required: false,
            type: Object,
            default: () => {
                return {};
            },
        },
    },
    data() {
        return {
            listinghash: '',
            isMap: new LocalStorage(`${this.name}_listing`, 'viewmap').get(),
            nameListing: this.name,
            listingMapData: {},
            maptoken: Env.get('jawg').token,
            activeToggleButton: 'liste-view',
        };
    },
    computed: {
        ...mapState('corp', {
            centermap: 'mainAddress',
        }),
        metaTitle() {
            return this.$route.meta && this.$route.meta.title != ''
                ? this.$route.meta.title
                : false;
        },
        primaryButtons() {
            return this.buttons.filter((button) => {
                return button.primary;
            });
        },

        secondaryButtons() {
            return this.buttons.filter((button) => {
                return button.primary == undefined || !button.primary;
            });
        },

        ajax() {
            return this.$store.state[this.listinghash].ajax;
        },

        error() {
            return this.$store.state[this.listinghash].error;
        },

        init() {
            return this.$store.state[this.listinghash].init;
        },

        rows() {
            if (typeof this.$store.state[this.listinghash].rows === 'object') {
                return Object.values(this.$store.state[this.listinghash].rows);
            }
            return this.$store.state[this.listinghash].rows;
        },

        pagination() {
            return this.$store.state[this.listinghash].pagination;
        },
        columns() {
            return this.$store.state[this.listinghash].columns;
        },
        display() {
            return this.$store.state[this.listinghash].display;
        },

        activeColumns() {
            return this.$store.getters[`${this.listinghash}/activeColumns`];
        },

        activeFilters() {
            return this.$store.getters[`${this.listinghash}/activeFilters`];
        },

        loading() {
            return !!this.ajax;
        },

        meta() {
            return this.$store.getters[`${this.listinghash}/meta`];
        },

        permissions() {
            return this.$store.getters[`${this.listinghash}/permissions`];
        },

        favoriteData() {
            return this.$store.state[this.listinghash].favoriteData;
        },

        kpis() {
            return this.$store.state[this.listinghash].kpis;
        },

        displayKpi() {
            return this.kpis.length > 0 && this.display.kpi;
        },

        hasKpis() {
            return this.$store.state[this.listinghash].hasKpis;
        },
    },

    watch: {
        $route(newRoute, oldRoute) {
            if (newRoute.path !== oldRoute.path) {
                this.initListing();
            }
        },
    },

    created() {
        if (this.isMap) {
            this.presetForMapDisplay('map-view');
        }
        this.initListing();
        EventBus.on('listing:refresh', this.fetch);
    },

    beforeUnmount() {
        this.$store.unregisterModule(this.listinghash);
        EventBus.off('listing:refresh', this.fetch);
    },

    methods: {
        ...popupPanelMapActions(['open']),
        goToUrl,
        handle_function_call(fnName, parameters, disabled) {
            disabled ? null : this[fnName](parameters);
        },
        callBus(event, disable, data) {
            disable ? null : EventBus.emit(event, data);
        },
        initListing() {
            this.listinghash = this.nameListing + '_' + guid();
            const store = this.$store;
            if (!(store && store.state && store.state[this.listinghash])) {
                let module = ListingModule;
                if (this.storeModule) {
                    module = this.storeModule;
                } else if (this.listingMapping) {
                    module = ApiListingModule;
                } else if (this.isMap) {
                    module = MapModule;
                }
                store.registerModule(this.listinghash, module);
            }

            this.$store.dispatch(`${this.listinghash}/updateListingHash`, this.listinghash);

            const payload = this.isMap
                ? { ...this.$props, name: this.nameListing }
                : { ...this.$props, favorite: this.$route.query.favorite };
            this.$store.dispatch(`${this.listinghash}/init`, payload).then(() => {
                this.fetch();
            });
        },
        // PAGINATION
        goToPage(page) {
            this.$store.commit(`${this.listinghash}/updateCurrentPage`, page);
            window.EventBus.emit('listing:updatePage', { listing: this.listinghash, page: page });
            this.fetch();
        },
        getButtonHref(button) {
            return buildUrl(button.href, [
                { key: 'linkedid', value: this.$store.getters.linkedid },
            ]);
        },
        // Main methods
        fetch(overwriteMeta) {
            this.$store.dispatch(`${this.listinghash}/fetch`, overwriteMeta).then(() => {
                if (this.activeFilters && this.activeFilters.length !== 0) {
                    this.$store.commit(`${this.listinghash}/updateFiltersView`, 'summary');
                }
                this.buttons.map((button) => {
                    this.checkPermissionsButton(button);
                });

                this.$emit('listinghash', this.listinghash);
                this.$forceUpdate();
            });

            if (this.useFavorites) {
                this.$store.dispatch(`${this.listinghash}/fetchFavorites`);
            }
        },
        checkPermissionsButton(button) {
            const hasPermission = this.permissions && button.permission && button.permission != '';
            const canPermission =
                hasPermission &&
                this.permissions[button.permission] &&
                this.permissions[button.permission] == 1;
            const hasRelatedPermission =
                this.relatedPermissions &&
                button.relatedPermission &&
                button.relatedPermission != '';
            const canRelatedPermission =
                hasRelatedPermission &&
                this.relatedPermissions[button.relatedPermission] &&
                this.relatedPermissions[button.relatedPermission] == 1;
            if (hasPermission && hasRelatedPermission) {
                button.disabled = canPermission && canRelatedPermission ? false : true;
            } else if (hasPermission) {
                button.disabled = canPermission ? false : true;
            } else {
                button.disabled = false;
            }
        },
        toggleKPI(e) {
            const display = { ...this.display };
            display.kpi = !display.kpi;

            // In case this listing has KPI, but not loaded on the page
            // we have to get them now
            if (display.kpi && this.hasKpis) {
                this.$store.dispatch(`${this.listinghash}/reloadKpis`);
            }

            if (this.$store.getters[`${this.listinghash}/isLoadingKpis`]) {
                const interval = setInterval(() => {
                    if (!this.$store.getters[`${this.listinghash}/isLoadingKpis`]) {
                        clearInterval(interval);
                        this.$store
                            .dispatch(`${this.listinghash}/updateDisplay`, display)
                            .then(() => {
                                this.$store.commit(`${this.listinghash}/updateDisplay`, display);
                            });
                    }
                }, 100);
            }

            if (!this.$store.getters[`${this.listinghash}/isLoadingKpis`]) {
                this.$store.dispatch(`${this.listinghash}/updateDisplay`, display).then(() => {
                    this.$store.commit(`${this.listinghash}/updateDisplay`, display);
                });
            }

            if (!display.kpi) {
                e.target.blur();
            }
        },

        presetForMapDisplay(idButton) {
            this.switchDisplay.map((display) => (display.selected = display.id == idButton));
            this.listingMapData = ConfigListings.getListingByName(this.name);
            this.nameListing = this.isMap ? this.listingMapData.mapname : this.name;
            this.activeToggleButton = idButton;
        },

        processChangeDisplay(idButton) {
            this.isMap = idButton === 'map-view';
            new LocalStorage(`${this.name}_listing`, 'viewmap').set(this.isMap);
            this.$store.unregisterModule(this.listinghash);
            this.presetForMapDisplay(idButton);
            this.initListing();
        },

        toggleMap(idButton) {
            this.processChangeDisplay(idButton);
        },

        openOpportunity(params) {
            this.open({
                key: 'opportunityForm',
                props: {
                    oppId: false,
                    mode: 'edit',
                    action: 'edit',
                    linkedid: params.linkedid,
                    linkedtype: params.linkedtype,
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;
@import '~@sellsy/styleguide/src/_transitions';

.flex-row {
    .leftpane {
        width: 280px;
    }
}
.placeholder-alert {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
    height: 40px;
}

.compass-toggle-button {
    &--is-active {
        border-color: var(--colors-common-border-default);
        color: var(--colors-common-text-standard-active);
        cursor: default;

        &:hover,
        &:focus {
            border-color: var(--colors-common-border-default);
            color: var(--colors-common-text-standard-active);
            cursor: default;
        }
    }
    &.upsell-action {
        background: var(--colors-component-background-actionable-disabled);
        color: var(--colors-common-text-standard-disabled);
        position: relative;

        &:hover,
        &:focus {
            background: var(--colors-component-background-actionable-disabled);
            border-color: var(--colors-common-border-default);
            color: var(--colors-common-text-standard-disabled);
        }

        &:deep(.compass-feature-badge) {
            height: 12px;
            position: absolute;
            right: 2px;
            top: 2px;
            width: 12px;

            svg {
                height: 7.2px;
                width: 7.2px;
            }
        }
    }
}

.kip-list {
    margin: calc(var(--spacing-1) * -1) 0 var(--spacing-0);
}
</style>

<style lang="scss">
#compass-menu-container {
    .listing-menu-settings {
        z-index: 3;
    }
}
</style>
